// Sentry client wrapper — thin shim over @sentry/browser so the rest
// of the app never has to import Sentry directly. Two reasons:
//   1. Init is env-gated (REACT_APP_SENTRY_DSN unset = silent no-op,
//      so local dev + preview builds don't spam Sentry).
//   2. Every callsite goes through captureError() which adds our own
//      tags (owner_id, org_id, tab, route) automatically — cutting the
//      per-report-plumbing noise.
//
// Used from src/index.js (RootErrorBoundary), src/CommissionTracker.jsx
// (TabBoundary via captureError from componentDidCatch), and any async
// path that wants explicit reporting.

import * as Sentry from '@sentry/browser';

let initialized = false;

import { askAdminForSre } from './askAdminForSre';

export function initSentry() {
  if (initialized) return;
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    // Silent no-op in envs without a DSN. Do NOT log — noise on every
    // dev boot.
    return;
  }
  try {
    Sentry.init({
      dsn,
      // Environment tag lets us split prod vs preview in the Sentry UI.
      // Vercel injects VERCEL_ENV = production | preview | development.
      environment: process.env.REACT_APP_VERCEL_ENV || process.env.NODE_ENV || 'unknown',
      // Release tag = the Vercel commit SHA so a stack points at the
      // exact build. Vercel injects VERCEL_GIT_COMMIT_SHA.
      release: process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA || undefined,
      // Traces/perf sampling — start conservative (5%) since this
      // multiplies the volume by ~everything the operator does. Tune
      // via env once we know what's coming through.
      tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.05),
      // Session Replay is BOTH bundle-expensive and privacy-hot (it
      // records the DOM). Leave off; enable per-session via
      // Sentry.getClient().setReplayIntegration if we ever want it.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // Filter out noisy known-benign errors here rather than paying
      // for them monthly.
      ignoreErrors: [
        /ResizeObserver loop /i,       // Chrome benign; never actionable
        /Non-Error promise rejection captured/i,
        /Load failed/i,                // Safari's message for AbortError on nav-away
      ],
      beforeSend(event) {
        // Strip any authorization headers that snuck into the breadcrumb.
        // Sentry's default scrubber catches "Authorization" case-insensitively,
        // but our fetch wrappers use lowercase everywhere — belt-and-braces.
        try {
          if (event.request?.headers) {
            const h = event.request.headers;
            for (const k of Object.keys(h)) {
              if (/^authorization$|^cookie$|^x-.*-token$/i.test(k)) h[k] = '[redacted]';
            }
          }
        } catch { /* never let the beforeSend hook throw */ }
        return event;
      },
    });
    initialized = true;
  } catch (err) {
    // Init failing is not fatal. Log so we notice, but do not throw.
    // eslint-disable-next-line no-console
    console.warn('[sentry] init failed:', err?.message || err);
  }
}

// Attach user + org identity so every subsequent capture is scoped.
// Call once after auth resolves (currentUser + org_id known).
export function setSentryUser({ id, email, org_id } = {}) {
  if (!initialized) return;
  try {
    Sentry.setUser(id ? { id, email } : null);
    if (org_id) Sentry.setTag('org_id', org_id);
  } catch { /* swallow */ }
}

// Universal capture entry point. Takes an Error + a context bag; the
// context becomes both tags (short scalars) and extra (everything else).
// If Sentry never initialized, still console.error so local dev sees it.
export function captureError(err, ctx = {}) {
  // Always fan out to the sfa-admin SRE ingest so the ops dashboard
  // sees the same errors Sentry does. Fire-and-forget; contract per
  // handoff from sfa-admin assistant 2026-07-11 (see
  // src/lib/askAdminForSre.js). Runs regardless of Sentry init state
  // so preview / dev builds without a Sentry DSN still emit.
  try {
    askAdminForSre({
      error:    err,
      route:    ctx?.route || (typeof window !== 'undefined' ? window.location?.pathname : null),
      level:    ctx?.level || 'error',
      owner_id: ctx?.owner_id || ctx?.user_id || null,
    });
  } catch { /* fire-and-forget — never let SRE fanout block Sentry */ }

  if (!initialized) {
    // eslint-disable-next-line no-console
    console.error('[capture]', err, ctx);
    return;
  }
  try {
    Sentry.withScope(scope => {
      // Short-string context becomes a tag (fast filter in Sentry UI).
      for (const [k, v] of Object.entries(ctx || {})) {
        if (v == null) continue;
        if (typeof v === 'string' && v.length < 60) scope.setTag(k, v);
        else scope.setExtra(k, v);
      }
      Sentry.captureException(err);
    });
  } catch { /* never let error reporting itself throw */ }
}

// Explicit message (non-Error) capture. Rarely needed — prefer throwing
// or wrapping in Error and calling captureError.
export function captureMessage(msg, level = 'info', ctx = {}) {
  if (!initialized) {
    // eslint-disable-next-line no-console
    console.log(`[${level}]`, msg, ctx);
    return;
  }
  try {
    Sentry.withScope(scope => {
      scope.setLevel(level);
      for (const [k, v] of Object.entries(ctx || {})) {
        if (v == null) continue;
        if (typeof v === 'string' && v.length < 60) scope.setTag(k, v);
        else scope.setExtra(k, v);
      }
      Sentry.captureMessage(msg);
    });
  } catch { /* swallow */ }
}
